/// <amd-module name="VSS/Diagnostics/PerformanceViewService" />
import { IObservable, Observable } from "VSS/Core/Observable";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IServerStatistics, ISplitTiming, IVssPerformanceService, IVssScenario } from "VSS/Platform/Performance";

/**
 * Format of performance data returned from server.
 */
export interface IPerfDialogMeasurement {
    name: string;
    duration?: number;
    expanded: boolean;
    startTime?: number;
    innerMeasurements?: IPerfDialogMeasurement[];
    properties?: any;
}

/**
 * Format of performance data for display.
 */
export interface IPerformanceStatistic {
    name: string;
    expanded: boolean;
    start?: number;
    duration?: number;
    /** Duration divided by the overall group duration (if specified) */
    percentage?: number;
    properties?: string;
    measurement?: IPerfDialogMeasurement;
    childrenStatistics?: IPerformanceStatistic[];
    hasChildren: boolean;
    depth: number;
}

/**
 * Data use to populate the performance statistics view.
 */
export interface IPerformanceView extends IObservable<IPerfDialogMeasurement> {
    performanceItems: IPerformanceStatistic[];
    isFastPageSwitchScenario: boolean;

    toggle: (measurement: IPerfDialogMeasurement) => boolean;
    getMeasurementDuration(measurementName: string): number;
}

interface WebPerformanceTimingNamedEntry {
    entryGroupName: string;
    entry: WebPerformanceTimingEntry;
}

interface WebPerformanceTimingEntry {
    duration?: number;
    startTime?: number;
    properties?: { [key: string]: any };
}

class PerformanceView extends Observable<IPerfDialogMeasurement> implements IPerformanceView {
    private initialized: boolean = false;
    private navigationStart: number;
    private perfData: { [key: string]: IPerfDialogMeasurement } | undefined;
    private scenarioData: IPerfDialogMeasurement;

    public performanceItems: IPerformanceStatistic[] = [];
    public isFastPageSwitchScenario: boolean;

    constructor(
        navigationStart: number,
        perfData: { [key: string]: IPerfDialogMeasurement } | undefined,
        completedScenarios: IVssScenario[],
        fpsScenario: boolean
    ) {
        super();

        this.isFastPageSwitchScenario = fpsScenario;
        this.navigationStart = navigationStart;
        this.processScenarioData(completedScenarios);
        this.perfData = perfData;
        if (this.perfData) {
            this.updateItems();
            this.initialized = true;
        }
    }

    public getMeasurementDuration = (measurementName: string): number => {
        if (this.perfData) {
            const measurement = this.perfData[measurementName];
            if (measurement && measurement.duration) {
                return Math.floor(measurement.duration);
            }
        }

        return 0;
    };

    public toggle = (measurement: IPerfDialogMeasurement): boolean => {
        if (measurement) {
            measurement.expanded = !measurement.expanded;

            // Recalculate the items which will be displayed.
            this.updateItems();

            this.notify(measurement, measurement.expanded ? "expand" : "collapse");
            return true;
        }

        return false;
    };

    // Create list of scenarios to display
    private processScenarioData(completedScenarios: IVssScenario[]): void {
        if (completedScenarios.length > 0) {
            this.scenarioData = {
                name: "Scenarios",
                expanded: true,
                properties: "",
                innerMeasurements: []
            };

            for (let i = 0, len = completedScenarios.length; i < len; i++) {
                const scenario = completedScenarios[i];
                const scenarioItem: IPerfDialogMeasurement = {
                    name: scenario.scenarioName,
                    startTime: scenario.startTime - this.navigationStart,
                    duration: scenario.elaspsedTicks,
                    properties: this.getPropertiesString(scenario.properties),
                    expanded: false
                };

                if (scenario.splitTimings.length > 0) {
                    for (const splitTiming of scenario.splitTimings) {
                        const splitItem: IPerfDialogMeasurement = {
                            name: splitTiming.name,
                            expanded: false,
                            properties: "",
                            startTime: splitTiming.timestamp
                        };

                        if (!scenarioItem.innerMeasurements) {
                            scenarioItem.innerMeasurements = [];
                        }
                        scenarioItem.innerMeasurements.push(splitItem);
                    }
                }

                if (this.scenarioData.innerMeasurements) {
                    this.scenarioData.innerMeasurements.push(scenarioItem);
                }
            }
        }
    }

    /**
     * Create all items to be displayed in the performance view
     */
    private updateItems(): void {
        const rootItems: IPerformanceStatistic[] = [];
        const allItems: IPerformanceStatistic[] = [];
        if (this.perfData) {
            const detailedGroups: { [key: string]: { timing: string; groups?: string[]; collapsed: boolean } } = {};

            if (this.isFastPageSwitchScenario) {
                detailedGroups["FPS Request"] = { timing: "FPS Request", collapsed: false };
            } else {
                detailedGroups["Pre-Request"] = { timing: "PreRequest", groups: ["Redirect", "Fetch", "DNS", "TCP"], collapsed: true };
                detailedGroups["Page Request"] = { timing: "RequestResponse", groups: ["Request", "Response"], collapsed: false };
            }
            detailedGroups["Client-side Rendering"] = { timing: "Client-side Rendering", collapsed: false };

            // Process timings for each detailed groups
            for (const detailedGroupName in detailedGroups) {
                const detailedGroup = detailedGroups[detailedGroupName];
                const groupMeasurement = this.perfData[detailedGroup.timing];

                // If the measurement exists,
                if (groupMeasurement) {
                    const item = this.addMeasureToGridItems(groupMeasurement, rootItems, true, groupMeasurement.duration);
                    if (!item.childrenStatistics) {
                        item.childrenStatistics = [];
                    }
                    item.expanded = this.initialized ? groupMeasurement.expanded : !detailedGroup.collapsed;
                    item["name"] = detailedGroupName;

                    if (detailedGroup.groups) {
                        for (const child of detailedGroup.groups) {
                            const measurement = this.perfData[child];
                            if (measurement) {
                                this.addMeasureToGridItems(measurement, item.childrenStatistics, true, groupMeasurement.duration);
                            }
                        }
                        item.hasChildren = true;
                    }

                    groupMeasurement.expanded = item.expanded;
                }
            }

            // Add performance scenario data
            if (this.scenarioData) {
                this.addMeasureToGridItems(this.scenarioData, rootItems, true);
            }

            this.createStatItems(allItems, rootItems, 0);
        }

        this.performanceItems = allItems;
    }

    private addMeasureToGridItems(
        measurement: IPerfDialogMeasurement,
        rootItems: IPerformanceStatistic[],
        isRootItem: boolean,
        overallDuration?: number
    ): IPerformanceStatistic {
        const measurementStartTime = measurement.startTime && measurement.startTime > 0 ? measurement.startTime : 0;
        const measurementDuration = measurement.duration && measurement.duration > 0 ? measurement.duration : 0;

        const duration = measurement.duration !== undefined && measurement.duration >= 0 ? Math.floor(measurementDuration) : undefined;
        const item: IPerformanceStatistic = {
            name: measurement.name,
            expanded: measurement.expanded,
            start: measurement.startTime !== undefined && measurement.startTime >= 0 ? Math.floor(measurementStartTime) : undefined,
            duration,
            percentage: duration && overallDuration && duration / overallDuration,
            properties: typeof measurement.properties === "string" ? measurement.properties : this.getPropertiesString(measurement.properties),
            measurement: measurement,
            hasChildren: false,
            depth: 0
        };

        // We're processing measurements in sorted order. Check which level in the tree they should be appended to
        let itemsForInsertion = rootItems;
        if (!isRootItem && measurementStartTime > 0) {
            while (itemsForInsertion.length > 0) {
                const compareItem = itemsForInsertion[itemsForInsertion.length - 1];
                const compareMeasurement: IPerfDialogMeasurement | undefined = compareItem["measurement"];
                if (
                    !compareMeasurement ||
                    !compareMeasurement.startTime ||
                    measurementStartTime >= compareMeasurement.startTime + (compareMeasurement.duration || 0) ||
                    measurementStartTime + measurementDuration >= compareMeasurement.startTime + (compareMeasurement.duration || 0)
                ) {
                    break;
                }

                if (!compareItem.childrenStatistics) {
                    compareItem.childrenStatistics = [];
                }
                itemsForInsertion = compareItem.childrenStatistics;
            }
        }
        itemsForInsertion.push(item);

        // Process inner measurements
        if (measurement.innerMeasurements) {
            item.childrenStatistics = [];
            item.hasChildren = true;

            if (measurement.expanded) {
                for (const childMeasurement of measurement.innerMeasurements) {
                    this.addMeasureToGridItems(childMeasurement, item.childrenStatistics, false, overallDuration);
                }
            }
        }

        return item;
    }

    /**
     * Flatten the tree into list to display in view.
     *
     * @param allItems
     * @param items
     * @param depth
     */
    private createStatItems(allItems: IPerformanceStatistic[], items: IPerformanceStatistic[], depth: number) {
        if (items && items.length > 0) {
            for (let i = 0; i < items.length; i++) {
                const statItem: IPerformanceStatistic = items[i];
                statItem.depth = depth;

                if (!statItem.hasChildren) {
                    statItem.hasChildren = statItem.childrenStatistics && statItem.childrenStatistics.length > 0 ? true : false;
                }
                allItems.push(statItem);

                if (statItem.childrenStatistics && statItem.expanded) {
                    this.createStatItems(allItems, statItem.childrenStatistics, depth + 1);
                }
            }
        }
    }

    private getPropertiesString(properties: any) {
        const pieces: string[] = [];
        if (properties) {
            for (const key in properties) {
                let entry = key;
                const value = properties[key];
                if (value !== null && typeof value !== "undefined") {
                    if (typeof value === "string") {
                        entry += ": " + value;
                    } else {
                        entry += ": " + JSON.stringify(value);
                    }
                }
                pieces.push(entry);
            }
        }
        return pieces.join("\n");
    }
}

export interface IVssPerformanceViewService extends IVssService {
    /**
     * Return view for tti scenario
     */
    getPerformanceView(): IPerformanceView;
}

class VssPerformanceViewService extends VssService implements IVssPerformanceViewService {
    private scenarioDescriptors: { [key: string]: IVssScenario } = {};
    private perfDataTtiScenario: string;
    private performanceService: IVssPerformanceService;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        const completedScenarios = this.performanceService.getCompletedScenarios();
        for (const completedScenario of completedScenarios) {
            this.processCompletedScenario(completedScenario);
        }

        this.performanceService.subscribe(this.processCompletedScenario, "scenarioComplete");
    }

    public _serviceEnd() {
        this.performanceService.unsubscribe(this.processCompletedScenario, "scenarioComplete");
    }

    public getPerformanceView(): IPerformanceView {
        return new PerformanceView(
            this.performanceService.getNavigationStartTime(),
            this.getMeasurements(this.perfDataTtiScenario),
            this.performanceService.getCompletedScenarios(),
            this.performanceService.isFastPageSwitchScenario()
        );
    }

    public getMeasurements(scenarioName: string): { [key: string]: IPerfDialogMeasurement } {
        const measurements: { [key: string]: IPerfDialogMeasurement } = {};
        const scenario = this.scenarioDescriptors[scenarioName];
        const perfData = this.performanceService.getServerStatistics();

        try {
            const expectedDefaultEvents = this.performanceService.getDefaultNavigationEvents();

            for (const expectedDefaultEvent of expectedDefaultEvents) {
                if (expectedDefaultEvent.perfEntry) {
                    const measurement: IPerfDialogMeasurement = {
                        name: expectedDefaultEvent.name,
                        duration: expectedDefaultEvent.perfEntry.duration,
                        expanded: false,
                        startTime: expectedDefaultEvent.perfEntry.startTime
                    };
                    measurements[measurement.name] = measurement;
                }
            }

            let fpsReceivedSplit: ISplitTiming | undefined = undefined;

            let requestMeasurement = measurements["Request"];

            // Update the page Request with the FPS request information if this is an FPS scenario
            if (this.performanceService.isFastPageSwitchScenario()) {
                const fpsRequest = {
                    name: "FPS Request",
                    expanded: true,
                    duration: 0,
                    startTime: 0
                } as IPerfDialogMeasurement;

                measurements[fpsRequest.name] = fpsRequest;

                if (scenario && scenario.splitTimings) {
                    fpsReceivedSplit = scenario.splitTimings.filter(t => t.name === "fpsResponseReceived")[0];
                    if (fpsReceivedSplit) {
                        fpsRequest.duration = fpsReceivedSplit.timestamp;
                    }
                }

                requestMeasurement = fpsRequest;
            }

            // Add server timings to the Request measurement
            if (requestMeasurement) {
                requestMeasurement.innerMeasurements = [];
                requestMeasurement.expanded = true;
                const serverTimings = this.convertPerfTimingsToTimingEntries(perfData);
                serverTimings.forEach(serverTiming => {
                    if (
                        requestMeasurement &&
                        requestMeasurement.innerMeasurements &&
                        requestMeasurement.startTime !== undefined &&
                        serverTiming &&
                        serverTiming.entry &&
                        serverTiming.entry.startTime
                    ) {
                        requestMeasurement.innerMeasurements.push({
                            name: serverTiming.entryGroupName,
                            duration: serverTiming.entry.duration,
                            expanded: false,
                            startTime: serverTiming.entry.startTime / 1000 + requestMeasurement.startTime,
                            properties: serverTiming.entry.properties
                        });
                    }
                });
            }

            if (scenario) {
                const responseEntries = this.performanceService.getEntriesByName("Response");
                if (responseEntries && responseEntries.length > 0) {
                    const responseEndtime = responseEntries[0].startTime + responseEntries[0].duration;

                    const clientSideRenderingMeasurement: IPerfDialogMeasurement = {
                        name: "Client-side Rendering",
                        startTime: responseEndtime,
                        duration: scenario.elaspsedTicks - responseEndtime,
                        expanded: false
                    };

                    if (fpsReceivedSplit) {
                        clientSideRenderingMeasurement.startTime = fpsReceivedSplit.timestamp;
                        clientSideRenderingMeasurement.duration = scenario.elaspsedTicks - fpsReceivedSplit.timestamp;
                    }

                    // only show the client-side rendering measurement if it's greater than 0. If it's less than 0, it means we don't have the TTI scenario.
                    if (clientSideRenderingMeasurement.duration && clientSideRenderingMeasurement.duration >= 0) {
                        const intialScenarioClientMeasurement = clientSideRenderingMeasurement;
                        measurements[intialScenarioClientMeasurement.name] = intialScenarioClientMeasurement;
                    }
                }
            }
        } catch (error) {}

        return measurements;
    }

    private processCompletedScenario = (scenario: IVssScenario): void => {
        this.scenarioDescriptors[scenario.scenarioName] = scenario;

        if (scenario.isPageInteractive) {
            this.perfDataTtiScenario = scenario.scenarioName;
        }
    };

    private convertPerfTimingsToTimingEntries(perfTimings: IServerStatistics | null): WebPerformanceTimingNamedEntry[] {
        const timingEntries: WebPerformanceTimingNamedEntry[] = [];

        if (perfTimings) {
            for (const perfId in perfTimings) {
                const group = perfTimings[perfId];
                group.timings.forEach(timing => {
                    timingEntries.push({
                        entryGroupName: perfId,
                        entry: {
                            duration: (timing.elapsedTicks || 0) / 10000,
                            startTime: timing.startOffset,
                            properties: timing.properties
                        }
                    });
                });
            }

            timingEntries.sort((a, b) => {
                if (a.entry.startTime && b.entry.startTime) {
                    return a.entry.startTime - b.entry.startTime;
                } else if (a.entry.startTime) {
                    return 1;
                } else {
                    return -1;
                }
            });
        }
        return timingEntries;
    }
}

Services.add("IVssPerformanceViewService", { serviceFactory: VssPerformanceViewService });
